import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import BackToTop from '@/components/feature/BackToTop';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

const serviceAreas = [
  {
    id: 'burbank',
    city: 'Burbank',
    state: 'CA',
    region: 'San Fernando Valley',
    zip: '91501',
    lat: 34.1808,
    lng: -118.3090,
    slug: 'electronics-recycling-burbank',
    description: 'Greenly Electronics Recycling serves Burbank, CA with free e-waste pickup for businesses and convenient drop-off options. We accept computers, TVs, batteries, and all electronics from Burbank residents and companies.',
    highlights: ['Free business pickup in Burbank', 'TV & computer recycling', 'Data destruction with certificate', 'Same-day pickup available'],
    nearbyAreas: ['North Hollywood', 'Glendale', 'Toluca Lake', 'Studio City'],
    image: 'https://readdy.ai/api/search-image?query=Burbank%20California%20sunny%20suburban%20boulevard%20lined%20with%20tall%20palm%20trees%20and%20modern%20commercial%20buildings%2C%20bright%20blue%20sky%2C%20clean%20wide%20street%2C%20professional%20business%20district%20near%20Hollywood%2C%20lush%20green%20landscaping%2C%20vibrant%20daytime%20cityscape%20wide%20angle&width=700&height=400&seq=area-burbank-v2&orientation=landscape',
  },
  {
    id: 'glendale',
    city: 'Glendale',
    state: 'CA',
    region: 'San Fernando Valley',
    zip: '91201',
    lat: 34.1425,
    lng: -118.2551,
    slug: 'electronics-recycling-glendale',
    description: 'Greenly provides certified electronics recycling and data destruction services throughout Glendale, CA. Our team picks up e-waste from Glendale offices, schools, and businesses — free of charge for qualifying loads.',
    highlights: ['Free e-waste pickup for Glendale businesses', 'HIPAA-compliant data destruction', 'CalRecycle certified service', 'Schools & medical facilities welcome'],
    nearbyAreas: ['Burbank', 'Pasadena', 'La Crescenta', 'Montrose'],
    image: 'https://readdy.ai/api/search-image?query=Glendale%20California%20downtown%20area%20with%20modern%20office%20buildings%20and%20tree-lined%20streets%2C%20sunny%20California%20day%2C%20clean%20urban%20neighborhood%2C%20San%20Fernando%20Valley%20city%2C%20professional%20business%20area%2C%20wide%20angle&width=700&height=400&seq=area-glendale&orientation=landscape',
  },
  {
    id: 'pasadena',
    city: 'Pasadena',
    state: 'CA',
    region: 'San Gabriel Valley',
    zip: '91101',
    lat: 34.1478,
    lng: -118.1445,
    slug: 'electronics-recycling-pasadena',
    description: 'Serving Pasadena and the San Gabriel Valley with responsible electronics recycling. Greenly picks up e-waste from Pasadena universities, hospitals, businesses, and homes — with certified data destruction available.',
    highlights: ['University & research lab cleanouts', 'Hospital-grade data destruction', 'Free pickup for large loads', 'Certificate of recycling provided'],
    nearbyAreas: ['Arcadia', 'Monrovia', 'Alhambra', 'San Marino'],
    image: 'https://readdy.ai/api/search-image?query=Pasadena%20California%20beautiful%20tree-lined%20street%20with%20historic%20architecture%20and%20palm%20trees%2C%20sunny%20day%2C%20upscale%20residential%20neighborhood%2C%20San%20Gabriel%20Valley%2C%20clean%20suburban%20community%2C%20wide%20angle%20street%20view&width=700&height=400&seq=area-pasadena&orientation=landscape',
  },
  {
    id: 'long-beach',
    city: 'Long Beach',
    state: 'CA',
    region: 'South Bay',
    zip: '90802',
    lat: 33.7701,
    lng: -118.1937,
    slug: 'electronics-recycling-long-beach',
    description: 'Greenly Electronics Recycling serves Long Beach with free e-waste pickup for businesses and organizations. We handle TV recycling, computer recycling, battery disposal, and hard drive shredding throughout Long Beach and the South Bay.',
    highlights: ['Free business pickup in Long Beach', 'All TV types accepted', 'Battery recycling specialists', 'Port-area industrial pickups'],
    nearbyAreas: ['Lakewood', 'Compton', 'Carson', 'Signal Hill'],
    image: 'https://readdy.ai/api/search-image?query=Long%20Beach%20California%20waterfront%20city%20skyline%20with%20palm%20trees%20and%20modern%20buildings%2C%20sunny%20day%2C%20coastal%20Southern%20California%20city%2C%20clean%20urban%20environment%2C%20wide%20angle%20view%20of%20downtown%20Long%20Beach&width=700&height=400&seq=area-longbeach&orientation=landscape',
  },
  {
    id: 'santa-monica',
    city: 'Santa Monica',
    state: 'CA',
    region: 'Westside',
    zip: '90401',
    lat: 34.0195,
    lng: -118.4912,
    slug: 'electronics-recycling-santa-monica',
    description: 'Greenly serves Santa Monica and the Westside with eco-friendly electronics recycling. We pick up e-waste from Santa Monica tech companies, startups, schools, and residents — helping keep the Westside green.',
    highlights: ['Tech company IT cleanouts', 'Startup office equipment pickup', 'Eco-certified recycling', 'Westside same-day service'],
    nearbyAreas: ['Venice', 'Culver City', 'Brentwood', 'Pacific Palisades'],
    image: 'https://readdy.ai/api/search-image?query=Santa%20Monica%20California%20sunny%20beach%20city%20with%20palm%20trees%20and%20modern%20buildings%2C%20clean%20coastal%20neighborhood%2C%20eco-friendly%20California%20community%2C%20blue%20sky%2C%20wide%20angle%20street%20view%20near%20the%20beach&width=700&height=400&seq=area-santamonica&orientation=landscape',
  },
  {
    id: 'culver-city',
    city: 'Culver City',
    state: 'CA',
    region: 'Westside',
    zip: '90230',
    lat: 34.0211,
    lng: -118.3965,
    slug: 'electronics-recycling-culver-city',
    description: 'Greenly provides electronics recycling and data destruction for Culver City businesses, studios, and tech companies. We offer free pickup for qualifying loads and certified hard drive shredding for media and entertainment companies.',
    highlights: ['Media & entertainment studio cleanouts', 'Certified data destruction', 'Free pickup for qualifying loads', 'Tech company IT asset disposal'],
    nearbyAreas: ['Santa Monica', 'Inglewood', 'West LA', 'Mar Vista'],
    image: 'https://readdy.ai/api/search-image?query=Culver%20City%20California%20modern%20urban%20neighborhood%20with%20creative%20office%20buildings%20and%20palm%20trees%2C%20sunny%20day%2C%20tech%20and%20entertainment%20district%2C%20clean%20streets%2C%20wide%20angle%20view%20of%20Culver%20City%20downtown%20area&width=700&height=400&seq=area-culvercity&orientation=landscape',
  },
  {
    id: 'inglewood',
    city: 'Inglewood',
    state: 'CA',
    region: 'South Bay',
    zip: '90301',
    lat: 33.9617,
    lng: -118.3531,
    slug: 'electronics-recycling-inglewood',
    description: 'Greenly Electronics Recycling serves Inglewood with free e-waste pickup and drop-off options. Our Slauson Ave location is just minutes from Inglewood — making it easy to recycle TVs, computers, and batteries responsibly.',
    highlights: ['Closest drop-off: 4902 W. Slauson Ave', 'Free business pickup', 'All electronics accepted', 'Community event hosting'],
    nearbyAreas: ['Hawthorne', 'Gardena', 'Lawndale', 'El Segundo'],
    image: 'https://readdy.ai/api/search-image?query=Inglewood%20California%20city%20street%20with%20modern%20buildings%20and%20palm%20trees%2C%20sunny%20day%2C%20South%20Bay%20Los%20Angeles%20neighborhood%2C%20clean%20urban%20community%2C%20wide%20angle%20street%20view%2C%20California%20sunshine&width=700&height=400&seq=area-inglewood&orientation=landscape',
  },
  {
    id: 'koreatown',
    city: 'Koreatown',
    state: 'CA',
    region: 'Central Los Angeles',
    zip: '90005',
    lat: 34.0584,
    lng: -118.3006,
    slug: null,
    description: 'Greenly\'s W. 3rd Street location is just minutes from Koreatown. We serve Koreatown businesses, apartment complexes, and residents with free e-waste drop-off and business pickup for electronics recycling and data destruction.',
    highlights: ['Closest drop-off: 2542 W. 3rd Street', 'Apartment complex cleanouts', 'Business IT equipment pickup', 'Walk-in drop-off welcome'],
    nearbyAreas: ['Mid-City', 'Westlake', 'Silver Lake', 'Downtown LA'],
    image: 'https://readdy.ai/api/search-image?query=Koreatown%20Los%20Angeles%20California%20busy%20urban%20street%20with%20colorful%20storefronts%20and%20high-rise%20buildings%2C%20sunny%20day%2C%20vibrant%20city%20neighborhood%2C%20Central%20Los%20Angeles%2C%20wide%20angle%20street%20view&width=700&height=400&seq=area-koreatown&orientation=landscape',
  },
  {
    id: 'downtown-los-angeles',
    city: 'Downtown Los Angeles',
    state: 'CA',
    region: 'Central Los Angeles',
    zip: '90012',
    lat: 34.0522,
    lng: -118.2437,
    slug: 'electronics-recycling-downtown-los-angeles',
    description: 'Greenly serves Downtown Los Angeles with certified electronics recycling and data destruction. We pick up e-waste from DTLA offices, law firms, financial institutions, and government agencies — with full chain-of-custody documentation.',
    highlights: ['Corporate office IT cleanouts', 'Law firm & financial data destruction', 'Government agency compliance', 'Same-day DTLA pickup available'],
    nearbyAreas: ['Little Tokyo', 'Arts District', 'Chinatown', 'Boyle Heights'],
    image: 'https://readdy.ai/api/search-image?query=Downtown%20Los%20Angeles%20California%20skyline%20with%20modern%20skyscrapers%20and%20palm%20trees%2C%20sunny%20day%2C%20urban%20business%20district%2C%20clean%20city%20streets%2C%20wide%20angle%20view%20of%20DTLA%20financial%20district&width=700&height=400&seq=area-dtla&orientation=landscape',
  },
  {
    id: 'san-fernando-valley',
    city: 'San Fernando Valley',
    state: 'CA',
    region: 'San Fernando Valley',
    zip: '91401',
    lat: 34.1836,
    lng: -118.4452,
    slug: null,
    description: 'Greenly covers the entire San Fernando Valley for electronics recycling and e-waste pickup. From Van Nuys to Chatsworth, we serve Valley businesses, schools, and residents with free pickup and certified recycling services.',
    highlights: ['Full Valley coverage', 'School & university pickups', 'Industrial & warehouse cleanouts', 'Free pickup for qualifying loads'],
    nearbyAreas: ['Van Nuys', 'Sherman Oaks', 'Encino', 'Chatsworth', 'Reseda'],
    image: 'https://readdy.ai/api/search-image?query=San%20Fernando%20Valley%20California%20suburban%20neighborhood%20with%20wide%20streets%20and%20palm%20trees%2C%20sunny%20day%2C%20residential%20Los%20Angeles%20area%2C%20clean%20suburban%20community%2C%20wide%20angle%20view%20of%20Valley%20neighborhood&width=700&height=400&seq=area-sfv&orientation=landscape',
  },
  {
    id: 'silver-lake',
    city: 'Silver Lake',
    state: 'CA',
    region: 'East Los Angeles',
    zip: '90026',
    lat: 34.0869,
    lng: -118.2706,
    slug: null,
    description: 'Greenly serves Silver Lake, Echo Park, and the Eastside with convenient electronics recycling. Our W. 3rd Street drop-off is minutes away, and we offer free business pickup for Silver Lake offices, studios, and creative companies.',
    highlights: ['Creative studio IT cleanouts', 'Close to W. 3rd St drop-off', 'Free business pickup', 'Eco-conscious community service'],
    nearbyAreas: ['Echo Park', 'Los Feliz', 'Atwater Village', 'Highland Park'],
    image: 'https://readdy.ai/api/search-image?query=Silver%20Lake%20Los%20Angeles%20California%20trendy%20neighborhood%20with%20colorful%20buildings%20and%20palm%20trees%2C%20sunny%20day%2C%20creative%20arts%20district%2C%20hip%20urban%20community%2C%20wide%20angle%20street%20view&width=700&height=400&seq=area-silverlake&orientation=landscape',
  },
  {
    id: 'torrance',
    city: 'Torrance',
    state: 'CA',
    region: 'South Bay',
    zip: '90501',
    lat: 33.8358,
    lng: -118.3406,
    slug: 'electronics-recycling-torrance',
    description: 'Greenly Electronics Recycling serves Torrance and the South Bay with free e-waste pickup for businesses and organizations. We handle corporate IT cleanouts, battery recycling, and certified data destruction for Torrance companies.',
    highlights: ['Corporate campus cleanouts', 'Battery recycling specialists', 'Free pickup for businesses', 'South Bay coverage'],
    nearbyAreas: ['Redondo Beach', 'Gardena', 'Carson', 'Lomita'],
    image: 'https://readdy.ai/api/search-image?query=Torrance%20California%20clean%20suburban%20city%20with%20modern%20office%20buildings%20and%20palm%20trees%2C%20sunny%20day%2C%20South%20Bay%20Los%20Angeles%20area%2C%20professional%20business%20district%2C%20wide%20angle%20street%20view&width=700&height=400&seq=area-torrance&orientation=landscape',
  },
  {
    id: 'redondo-beach',
    city: 'Redondo Beach',
    state: 'CA',
    region: 'South Bay',
    zip: '90277',
    lat: 33.8492,
    lng: -118.3884,
    slug: 'electronics-recycling-redondo-beach',
    description: 'Greenly Electronics Recycling serves Redondo Beach with free e-waste pickup for businesses and organizations. From waterfront offices to commercial districts, we handle TV recycling, computer recycling, battery disposal, and certified data destruction throughout Redondo Beach.',
    highlights: ['Waterfront & harbor business pickups', 'Free pickup for qualifying loads', 'Certified data destruction', 'South Bay same-week service'],
    nearbyAreas: ['Torrance', 'Hermosa Beach', 'Manhattan Beach', 'Palos Verdes', 'Gardena', 'Hawthorne'],
    image: 'https://readdy.ai/api/search-image?query=Redondo%20Beach%20California%20coastal%20city%20with%20palm%20trees%20and%20modern%20buildings%2C%20sunny%20day%2C%20South%20Bay%20Los%20Angeles%20beach%20community%2C%20clean%20suburban%20neighborhood%2C%20wide%20angle%20view%20of%20Redondo%20Beach%20waterfront%20area&width=700&height=400&seq=area-redondobeach&orientation=landscape',
  },
  {
    id: 'van-nuys',
    city: 'Van Nuys',
    state: 'CA',
    region: 'San Fernando Valley',
    zip: '91401',
    lat: 34.1867,
    lng: -118.4487,
    slug: 'electronics-recycling-van-nuys',
    description: 'Greenly serves Van Nuys and the San Fernando Valley with free e-waste pickup for businesses, warehouses, and organizations. We handle large-scale IT equipment disposals, battery recycling, and certified data destruction for Van Nuys companies.',
    highlights: ['Industrial & warehouse cleanouts', 'Large-scale IT disposals', 'Free pickup for businesses', 'Full Valley coverage'],
    nearbyAreas: ['Sherman Oaks', 'North Hollywood', 'Encino', 'Reseda', 'Panorama City', 'Studio City'],
    image: 'https://readdy.ai/api/search-image?query=Van%20Nuys%20California%20San%20Fernando%20Valley%20city%20street%20with%20commercial%20buildings%20and%20palm%20trees%2C%20sunny%20day%2C%20suburban%20Los%20Angeles%20business%20district%2C%20wide%20streets%2C%20wide%20angle%20view%20of%20Valley%20neighborhood&width=700&height=400&seq=area-vannuys&orientation=landscape',
  },
  {
    id: 'hawthorne',
    city: 'Hawthorne',
    state: 'CA',
    region: 'South Bay',
    zip: '90250',
    lat: 33.9164,
    lng: -118.3526,
    slug: 'electronics-recycling-hawthorne',
    description: 'Greenly Electronics Recycling serves Hawthorne with free e-waste pickup for businesses and organizations. Our Slauson Ave drop-off is just 5 miles away — making it one of the most convenient recycling options for Hawthorne residents and businesses.',
    highlights: ['Closest drop-off: 4902 W. Slauson Ave (5 mi)', 'Aerospace & tech company pickups', 'Free business pickup', 'All electronics accepted'],
    nearbyAreas: ['Lawndale', 'Gardena', 'Inglewood', 'El Segundo', 'Lennox', 'Westchester'],
    image: 'https://readdy.ai/api/search-image?query=Hawthorne%20California%20South%20Bay%20city%20street%20with%20modern%20commercial%20buildings%20and%20palm%20trees%2C%20sunny%20day%2C%20suburban%20Los%20Angeles%20neighborhood%2C%20clean%20urban%20community%2C%20wide%20angle%20street%20view%2C%20California%20sunshine&width=700&height=400&seq=area-hawthorne&orientation=landscape',
  },
];

const ServiceAreasJsonLd = () => (
  <>
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${SITE_URL}/service-areas#webpage`,
          "url": `${SITE_URL}/service-areas`,
          "name": "Electronics Recycling Service Areas | Los Angeles County | Greenly",
          "description": "Greenly Electronics Recycling serves all of Los Angeles County — Burbank, Glendale, Pasadena, Long Beach, Santa Monica, Culver City, Inglewood, Koreatown, DTLA, San Fernando Valley, Silver Lake, Torrance and more.",
          "isPartOf": { "@id": `${SITE_URL}/#website` },
          "about": { "@id": `${SITE_URL}/#business` },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
              { "@type": "ListItem", "position": 2, "name": "Service Areas", "item": `${SITE_URL}/service-areas` }
            ]
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${SITE_URL}/service-areas#breadcrumb`,
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "Service Areas", "item": `${SITE_URL}/service-areas` }
          ]
        },
        ...serviceAreas.map((area) => ({
          "@type": "LocalBusiness",
          "@id": `${SITE_URL}/service-areas#${area.id}`,
          "name": `Greenly Electronics Recycling — ${area.city}`,
          "description": area.description,
          "url": `${SITE_URL}/service-areas#${area.id}`,
          "telephone": "+12136001000",
          "email": "info@greenly.us",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": area.city,
            "addressRegion": area.state,
            "postalCode": area.zip,
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": area.lat,
            "longitude": area.lng
          },
          "areaServed": [
            { "@type": "City", "name": area.city },
            ...area.nearbyAreas.map((n) => ({ "@type": "City", "name": n }))
          ],
          "parentOrganization": { "@id": `${SITE_URL}/#business` },
          "priceRange": "Free–$$",
          "openingHoursSpecification": [
            { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "08:00", "closes": "17:00" },
            { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "09:00", "closes": "14:00" }
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": `Electronics Recycling Services in ${area.city}`,
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": `TV Recycling ${area.city}` } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": `Computer Recycling ${area.city}` } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": `Battery Recycling ${area.city}` } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": `Data Destruction ${area.city}` } }
            ]
          }
        }))
      ]
    })}</script>
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Do you serve all cities in Los Angeles County?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Greenly Electronics Recycling provides free business pickup throughout Los Angeles County, including Burbank, Glendale, Pasadena, Long Beach, Santa Monica, Culver City, Inglewood, and all surrounding cities. Residential customers can drop off at either of our two LA locations.",
          },
        },
        {
          "@type": "Question",
          "name": "Is there a minimum quantity for free pickup?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Free pickup is available for businesses with a qualifying load — typically 10 or more devices, or a significant volume of e-waste. For smaller quantities, we recommend dropping off at our W. 3rd Street or Slauson Ave locations. Call (213) 600-1000 to confirm.",
          },
        },
        {
          "@type": "Question",
          "name": "How far outside Los Angeles do you travel for pickup?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We primarily serve Los Angeles County. For locations in Orange County, Ventura County, or the Inland Empire, please call us at (213) 600-1000 to discuss options — we may be able to accommodate larger loads.",
          },
        },
        {
          "@type": "Question",
          "name": "Can I drop off electronics from outside Los Angeles?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolutely. Our drop-off locations at 2542 W. 3rd Street and 4902 W. Slauson Ave are open to anyone — no matter where you live or work. No appointment needed.",
          },
        },
      ],
    })}</script>
  </>
);

const ServiceAreasPage = () => (
  <>
    <title>Electronics Recycling Service Areas | Los Angeles County | Greenly</title>
    <meta name="description" content="Greenly Electronics Recycling serves all of LA County — Burbank, Glendale, Pasadena, Long Beach, Santa Monica, Culver City, Inglewood, DTLA & more. Free business pickup. Call (213) 600-1000." />
    <meta name="keywords" content="electronics recycling Los Angeles County, e-waste pickup Burbank, e-waste recycling Glendale, computer recycling Pasadena, TV recycling Long Beach, electronics recycling Santa Monica" />
    <meta name="robots" content="index, follow" />
    <meta name="last-modified" content="2026-05-11" />
    <link rel="canonical" href={`${SITE_URL}/service-areas`} />
    <ServiceAreasJsonLd />
    <Navbar />
    <main>
      {/* Hero */}
      <section className="bg-[#0F3D22] pt-28 pb-16 px-4 md:px-6 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/hero-service-areas.jpg"
            alt="Los Angeles County electronics recycling service area"
            className="w-full h-full object-cover object-center opacity-15"
            loading="eager"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto">
          <Breadcrumb items={[{ label: 'Service Areas' }]} />
          <div className="max-w-3xl mt-4">
            <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4">
              <i className="ri-map-pin-2-fill"></i>
              Serving All of Los Angeles County
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4">
              Electronics Recycling<br />Across Los Angeles County
            </h1>
            <p className="text-green-200 text-base leading-relaxed mb-6 max-w-2xl">
              Greenly Electronics Recycling provides <strong className="text-white">free e-waste pickup</strong> for businesses and organizations throughout Los Angeles County — from Burbank to Long Beach, Santa Monica to Pasadena. Two convenient drop-off locations serve the entire region.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="tel:+12136001000"
                className="inline-flex items-center gap-2 bg-white text-[#1B6B3A] font-bold px-6 py-3 rounded-md hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-phone-fill"></i>
                Call (213) 600-1000
              </a>
              <Link
                to="/pickup-request"
                className="inline-flex items-center gap-2 bg-[#4CAF82] text-white font-bold px-6 py-3 rounded-md hover:bg-[#3d9e70] transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-truck-line"></i>
                Request Free Pickup
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-[#1B6B3A] py-5">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-12 text-white text-xs md:text-sm">
            {[
              { icon: 'ri-map-pin-2-fill', text: '15+ Cities Served' },
              { icon: 'ri-building-2-line', text: 'Businesses & Residents' },
              { icon: 'ri-award-line', text: 'CalRecycle CEWD-127531' },
              { icon: 'ri-truck-line', text: 'Free Business Pickup' },
              { icon: 'ri-map-pin-line', text: '2 Drop-Off Locations' },
            ].map((b) => (
              <div key={b.text} className="flex items-center gap-2">
                <i className={`${b.icon} text-[#74C69D]`}></i>
                <span className="font-semibold">{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Drop-off locations reminder */}
      <section className="py-10 bg-[#F4FAF7]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-7">
            <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Drop-Off Locations</div>
            <h2 className="text-xl md:text-2xl font-extrabold text-gray-900">
              2 Convenient Drop-Off Centers Serving All of LA County
            </h2>
            <p className="text-gray-500 text-sm mt-2 max-w-xl mx-auto">
              No appointment needed. Drive up and drop off your electronics for free.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto">
            {[
              {
                name: 'Central LA — W. 3rd Street',
                addr: '2542 W. 3rd Street, Los Angeles, CA 90057',
                hours: 'Mon–Fri 8am–5pm · Sat 9am–2pm',
                serves: 'Koreatown, DTLA, Silver Lake, Mid-City, Westlake',
                link: 'https://maps.app.goo.gl/1GFb4ZnoPCREHhzy6',
              },
              {
                name: 'South LA — W. Slauson Ave',
                addr: '4902 W. Slauson Ave, Los Angeles, CA 90056',
                hours: 'Mon–Fri 8am–5pm · Sat 9am–2pm',
                serves: 'Inglewood, Culver City, Baldwin Hills, Ladera Heights',
                link: 'https://maps.google.com/?q=4902+W+Slauson+Ave+Los+Angeles+CA+90056',
              },
            ].map((loc) => (
              <div key={loc.name} className="bg-white border border-gray-200 rounded-2xl p-6">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-xl shrink-0">
                    <i className="ri-map-pin-2-fill text-[#1B6B3A] text-lg"></i>
                  </div>
                  <div>
                    <div className="font-extrabold text-gray-900 text-sm">{loc.name}</div>
                    <div className="text-gray-500 text-xs mt-0.5">{loc.addr}</div>
                    <div className="text-gray-400 text-xs mt-0.5">{loc.hours}</div>
                  </div>
                </div>
                <div className="text-xs text-[#1B6B3A] font-semibold mb-3">
                  <i className="ri-community-line mr-1"></i>Serves: {loc.serves}
                </div>
                <a
                  href={loc.link}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center gap-1.5 text-[#1B6B3A] font-semibold text-xs hover:underline cursor-pointer"
                >
                  <i className="ri-direction-line"></i>Get Directions
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas Grid */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Coverage Map</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
              Electronics Recycling in Every Corner of LA County
            </h2>
            <p className="text-gray-500 text-sm mt-2 max-w-2xl mx-auto">
              From the San Fernando Valley to the South Bay, from the Westside to the San Gabriel Valley — Greenly picks up e-waste and recycles electronics responsibly across all of Los Angeles County.
            </p>
          </div>

          <div className="space-y-10">
            {serviceAreas.map((area, idx) => (
              <article
                key={area.id}
                id={area.id}
                className={`flex flex-col lg:flex-row items-start gap-8 lg:gap-12 ${idx % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
              >
                {/* Image */}
                <div className="w-full lg:w-[42%] shrink-0 rounded-2xl overflow-hidden">
                  <img
                    src={area.image}
                    alt={`Electronics recycling in ${area.city}, CA`}
                    className="w-full h-56 md:h-64 object-cover object-top"
                    loading="lazy"
                    width={700}
                    height={400}
                  />
                </div>

                {/* Content */}
                <div className="w-full lg:w-[58%]">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center gap-1.5 bg-[#E8F5EE] text-[#1B6B3A] text-xs font-bold px-3 py-1 rounded-full">
                      <i className="ri-map-pin-line"></i>{area.region}
                    </span>
                  </div>
                  <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 mb-3">
                    Electronics Recycling in <strong>{area.city}</strong>, CA
                  </h2>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{area.description}</p>

                  {/* Highlights */}
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
                    {area.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2 text-sm text-gray-700">
                        <div className="w-5 h-5 flex items-center justify-center bg-[#E8F5EE] rounded-full shrink-0 mt-0.5">
                          <i className="ri-check-line text-[#1B6B3A] text-xs"></i>
                        </div>
                        {h}
                      </li>
                    ))}
                  </ul>

                  {/* Nearby areas */}
                  <div className="mb-5">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Also serving nearby: </span>
                    <span className="text-xs text-gray-500">{area.nearbyAreas.join(', ')}</span>
                  </div>

                  {/* CTAs */}
                  <div className="flex flex-wrap gap-3">
                    {area.slug && (
                      <Link
                        to={`/${area.slug}`}
                        className="inline-flex items-center gap-2 bg-[#1B6B3A] hover:bg-[#2D8653] text-white font-bold px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
                      >
                        <i className="ri-map-pin-line"></i>
                        {area.city} City Page
                      </Link>
                    )}
                    <Link
                      to="/pickup-request"
                      className={`inline-flex items-center gap-2 font-bold px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm ${area.slug ? 'border border-gray-200 hover:border-[#1B6B3A] text-gray-700 hover:text-[#1B6B3A]' : 'bg-[#1B6B3A] hover:bg-[#2D8653] text-white'}`}
                    >
                      <i className="ri-truck-line"></i>
                      Request Pickup in {area.city}
                    </Link>
                    <Link
                      to="/locations"
                      className="inline-flex items-center gap-2 border border-gray-200 hover:border-[#1B6B3A] text-gray-700 hover:text-[#1B6B3A] font-semibold px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
                    >
                      <i className="ri-map-pin-line"></i>
                      Find Drop-Off Location
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Services we offer everywhere */}
      <section className="py-14 bg-[#F4FAF7]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">What We Offer</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
              Full-Service Electronics Recycling Across LA County
            </h2>
            <p className="text-gray-500 text-sm mt-2 max-w-xl mx-auto">
              Every service we offer is available throughout Los Angeles County — wherever you are.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: 'ri-tv-line',
                title: 'TV Recycling',
                desc: 'CRT TVs, flat screens, plasma, OLED — all TV types accepted at both drop-off locations or via free business pickup.',
                path: '/tv-recycling',
              },
              {
                icon: 'ri-computer-line',
                title: 'Computer Recycling',
                desc: 'Desktops, laptops, Macs, servers, and monitors. Free data destruction included with every computer recycled.',
                path: '/computer-recycling',
              },
              {
                icon: 'ri-battery-charge-line',
                title: 'Battery Recycling',
                desc: 'Laptop batteries, phone batteries, lithium-ion, car batteries, and all rechargeable battery types.',
                path: '/battery-recycling',
              },
              {
                icon: 'ri-hard-drive-line',
                title: 'Data Destruction',
                desc: 'Certified hard drive shredding with Certificate of Destruction. HIPAA compliant. On-site and drop-off available.',
                path: '/hard-drive-destruction',
              },
            ].map((s) => (
              <Link
                key={s.path}
                to={s.path}
                className="bg-white rounded-xl p-6 hover:border-[#1B6B3A] border border-transparent transition-colors cursor-pointer group"
              >
                <div className="w-11 h-11 flex items-center justify-center bg-[#E8F5EE] group-hover:bg-[#1B6B3A] rounded-xl mb-4 transition-colors">
                  <i className={`${s.icon} text-[#1B6B3A] group-hover:text-white text-xl transition-colors`}></i>
                </div>
                <h3 className="font-extrabold text-gray-900 text-sm mb-2">{s.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
                <div className="mt-3 flex items-center gap-1 text-[#1B6B3A] text-xs font-bold">
                  Learn more <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Common Questions</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
              Service Area FAQs
            </h2>
          </div>
          <div className="space-y-4">
            {[
              {
                q: 'Do you serve all cities in Los Angeles County?',
                a: 'Yes. Greenly Electronics Recycling provides free business pickup throughout Los Angeles County, including Burbank, Glendale, Pasadena, Long Beach, Santa Monica, Culver City, Inglewood, and all surrounding cities. Residential customers can drop off at either of our two LA locations.',
              },
              {
                q: 'Is there a minimum quantity for free pickup?',
                a: 'Free pickup is available for businesses with a qualifying load — typically 10 or more devices, or a significant volume of e-waste. For smaller quantities, we recommend dropping off at our W. 3rd Street or Slauson Ave locations. Call (213) 600-1000 to confirm.',
              },
              {
                q: 'How far outside Los Angeles do you travel for pickup?',
                a: 'We primarily serve Los Angeles County. For locations in Orange County, Ventura County, or the Inland Empire, please call us at (213) 600-1000 to discuss options — we may be able to accommodate larger loads.',
              },
              {
                q: 'Can I drop off electronics from outside Los Angeles?',
                a: 'Absolutely. Our drop-off locations at 2542 W. 3rd Street and 4902 W. Slauson Ave are open to anyone — no matter where you live or work. No appointment needed.',
              },
            ].map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-xl p-5">
                <h4 className="font-extrabold text-gray-900 text-sm mb-2">{faq.q}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#1B6B3A]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
            Ready to Recycle Electronics in Your City?
          </h2>
          <p className="text-green-100 text-sm md:text-base mb-8 max-w-xl mx-auto">
            Call us or request a free pickup online. We serve all of Los Angeles County — businesses, schools, hospitals, and residents.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="tel:+12136001000"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#1B6B3A] font-bold px-8 py-3 rounded-md hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-phone-fill"></i>Call (213) 600-1000
            </a>
            <Link
              to="/pickup-request"
              className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white/40 text-white font-semibold px-8 py-3 rounded-md hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-truck-line"></i>Request Free Pickup
            </Link>
            <Link
              to="/locations"
              className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white/40 text-white font-semibold px-8 py-3 rounded-md hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-map-pin-line"></i>Find Drop-Off Location
            </Link>
          </div>
        </div>
      </section>
    </main>
    <Footer />
    <BackToTop />
  </>
);

export default ServiceAreasPage;
